import React, { useState } from 'react'
import { ChevronDown, ChevronUp, Palette, Gem, Camera, Sparkles, Utensils, Zap, Star, Filter, Zap as Lightning, Scissors, Feather, Layers, Lock, Briefcase, PartyPopper, Pill, RotateCcw, Footprints } from 'lucide-react'
import { FilterOptions, FilterCategory } from '../types'

interface FilterPanelProps {
  filters: FilterOptions
  onFilterChange: (filterType: keyof FilterOptions, value: string) => void
}

const FilterPanel: React.FC<FilterPanelProps> = ({ filters, onFilterChange }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())

  const filterCategories: FilterCategory[] = [
    {
      id: 'background',
      title: 'Background',
      options: [
        { value: 'beach', label: 'Beach' },
        { value: 'studio', label: 'Studio' },
        { value: 'bedroom', label: 'Bedroom' },
        { value: 'outdoor', label: 'Outdoor' },
        { value: 'luxury', label: 'Luxury' },
        { value: 'minimal', label: 'Minimal' }
      ]
    },
    {
      id: 'nailColor',
      title: 'Nail Polish',
      options: [
        { value: 'red', label: 'Red' },
        { value: 'pink', label: 'Pink' },
        { value: 'purple', label: 'Purple' },
        { value: 'blue', label: 'Blue' },
        { value: 'green', label: 'Green' },
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'gold', label: 'Gold' },
        { value: 'silver', label: 'Silver' },
        { value: 'french', label: 'French Tips' },
        { value: 'gradient', label: 'Gradient' }
      ]
    },
    {
      id: 'jewelry',
      title: 'Jewelry',
      options: [
        { value: 'anklet', label: 'Anklet' },
        { value: 'toe-ring', label: 'Toe Ring' },
        { value: 'foot-chain', label: 'Foot Chain' },
        { value: 'ankle-bracelet', label: 'Ankle Bracelet' },
        { value: 'pearls', label: 'Pearls' },
        { value: 'crystals', label: 'Crystals' },
        { value: 'gold-jewelry', label: 'Gold Jewelry' },
        { value: 'silver-jewelry', label: 'Silver Jewelry' }
      ]
    },
    {
      id: 'shoes',
      title: 'Shoes & Footwear',
      options: [
        // Athletic & Sports
        { value: 'sneakers', label: 'Sneakers' },
        { value: 'running-shoes', label: 'Running Shoes' },
        { value: 'basketball-shoes', label: 'Basketball Shoes' },
        { value: 'tennis-shoes', label: 'Tennis Shoes' },
        { value: 'cross-trainers', label: 'Cross Trainers' },
        { value: 'gym-shoes', label: 'Gym Shoes' },
        { value: 'hiking-boots', label: 'Hiking Boots' },
        { value: 'trail-runners', label: 'Trail Runners' },
        { value: 'climbing-shoes', label: 'Climbing Shoes' },
        { value: 'soccer-cleats', label: 'Soccer Cleats' },
        { value: 'football-cleats', label: 'Football Cleats' },
        { value: 'baseball-cleats', label: 'Baseball Cleats' },
        { value: 'golf-shoes', label: 'Golf Shoes' },
        { value: 'bowling-shoes', label: 'Bowling Shoes' },
        { value: 'skateboard-shoes', label: 'Skateboard Shoes' },
        { value: 'wrestling-shoes', label: 'Wrestling Shoes' },
        { value: 'boxing-shoes', label: 'Boxing Shoes' },
        { value: 'cycling-shoes', label: 'Cycling Shoes' },
        { value: 'water-shoes', label: 'Water Shoes' },
        { value: 'swim-fins', label: 'Swim Fins' },
        
        // Heels & Formal
        { value: 'stilettos', label: 'Stilettos' },
        { value: 'pumps', label: 'Pumps' },
        { value: 'platform-heels', label: 'Platform Heels' },
        { value: 'wedge-heels', label: 'Wedge Heels' },
        { value: 'kitten-heels', label: 'Kitten Heels' },
        { value: 'block-heels', label: 'Block Heels' },
        { value: 'cone-heels', label: 'Cone Heels' },
        { value: 'needle-heels', label: 'Needle Heels' },
        { value: 'chunky-heels', label: 'Chunky Heels' },
        { value: 'strappy-heels', label: 'Strappy Heels' },
        { value: 'ankle-strap-heels', label: 'Ankle Strap Heels' },
        { value: 'mary-jane-heels', label: 'Mary Jane Heels' },
        { value: 'peep-toe-heels', label: 'Peep Toe Heels' },
        { value: 'open-toe-heels', label: 'Open Toe Heels' },
        { value: 'closed-toe-heels', label: 'Closed Toe Heels' },
        { value: 'pointed-toe-heels', label: 'Pointed Toe Heels' },
        { value: 'round-toe-heels', label: 'Round Toe Heels' },
        { value: 'square-toe-heels', label: 'Square Toe Heels' },
        { value: 'slingback-heels', label: 'Slingback Heels' },
        { value: 'mule-heels', label: 'Mule Heels' },
        
        // Boots
        { value: 'ankle-boots', label: 'Ankle Boots' },
        { value: 'knee-high-boots', label: 'Knee High Boots' },
        { value: 'thigh-high-boots', label: 'Thigh High Boots' },
        { value: 'over-knee-boots', label: 'Over Knee Boots' },
        { value: 'combat-boots', label: 'Combat Boots' },
        { value: 'military-boots', label: 'Military Boots' },
        { value: 'work-boots', label: 'Work Boots' },
        { value: 'steel-toe-boots', label: 'Steel Toe Boots' },
        { value: 'cowboy-boots', label: 'Cowboy Boots' },
        { value: 'western-boots', label: 'Western Boots' },
        { value: 'riding-boots', label: 'Riding Boots' },
        { value: 'equestrian-boots', label: 'Equestrian Boots' },
        { value: 'rain-boots', label: 'Rain Boots' },
        { value: 'wellington-boots', label: 'Wellington Boots' },
        { value: 'rubber-boots', label: 'Rubber Boots' },
        { value: 'snow-boots', label: 'Snow Boots' },
        { value: 'winter-boots', label: 'Winter Boots' },
        { value: 'ugg-boots', label: 'UGG Boots' },
        { value: 'fur-boots', label: 'Fur Boots' },
        { value: 'shearling-boots', label: 'Shearling Boots' },
        { value: 'lace-up-boots', label: 'Lace Up Boots' },
        { value: 'zip-up-boots', label: 'Zip Up Boots' },
        { value: 'pull-on-boots', label: 'Pull On Boots' },
        { value: 'chelsea-boots', label: 'Chelsea Boots' },
        { value: 'chukka-boots', label: 'Chukka Boots' },
        { value: 'desert-boots', label: 'Desert Boots' },
        { value: 'doc-martens', label: 'Doc Martens' },
        { value: 'platform-boots', label: 'Platform Boots' },
        { value: 'goth-boots', label: 'Goth Boots' },
        { value: 'punk-boots', label: 'Punk Boots' },
        { value: 'biker-boots', label: 'Biker Boots' },
        { value: 'motorcycle-boots', label: 'Motorcycle Boots' },
        
        // Sandals
        { value: 'flip-flops', label: 'Flip Flops' },
        { value: 'thong-sandals', label: 'Thong Sandals' },
        { value: 'slide-sandals', label: 'Slide Sandals' },
        { value: 'slip-on-sandals', label: 'Slip On Sandals' },
        { value: 'strappy-sandals', label: 'Strappy Sandals' },
        { value: 'gladiator-sandals', label: 'Gladiator Sandals' },
        { value: 'roman-sandals', label: 'Roman Sandals' },
        { value: 'platform-sandals', label: 'Platform Sandals' },
        { value: 'wedge-sandals', label: 'Wedge Sandals' },
        { value: 'espadrille-sandals', label: 'Espadrille Sandals' },
        { value: 'birkenstock-sandals', label: 'Birkenstock Sandals' },
        { value: 'cork-sandals', label: 'Cork Sandals' },
        { value: 'leather-sandals', label: 'Leather Sandals' },
        { value: 'suede-sandals', label: 'Suede Sandals' },
        { value: 'metallic-sandals', label: 'Metallic Sandals' },
        { value: 'jeweled-sandals', label: 'Jeweled Sandals' },
        { value: 'beaded-sandals', label: 'Beaded Sandals' },
        { value: 'ankle-strap-sandals', label: 'Ankle Strap Sandals' },
        { value: 'toe-ring-sandals', label: 'Toe Ring Sandals' },
        { value: 'barefoot-sandals', label: 'Barefoot Sandals' },
        { value: 'beach-sandals', label: 'Beach Sandals' },
        { value: 'sport-sandals', label: 'Sport Sandals' },
        { value: 'hiking-sandals', label: 'Hiking Sandals' },
        { value: 'water-sandals', label: 'Water Sandals' },
        
        // Flats & Casual
        { value: 'ballet-flats', label: 'Ballet Flats' },
        { value: 'pointed-flats', label: 'Pointed Flats' },
        { value: 'round-toe-flats', label: 'Round Toe Flats' },
        { value: 'square-toe-flats', label: 'Square Toe Flats' },
        { value: 'cap-toe-flats', label: 'Cap Toe Flats' },
        { value: 'bow-flats', label: 'Bow Flats' },
        { value: 'mary-jane-flats', label: 'Mary Jane Flats' },
        { value: 'slip-on-flats', label: 'Slip On Flats' },
        { value: 'lace-up-flats', label: 'Lace Up Flats' },
        { value: 'oxford-flats', label: 'Oxford Flats' },
        { value: 'loafers', label: 'Loafers' },
        { value: 'penny-loafers', label: 'Penny Loafers' },
        { value: 'tassel-loafers', label: 'Tassel Loafers' },
        { value: 'bit-loafers', label: 'Bit Loafers' },
        { value: 'driving-loafers', label: 'Driving Loafers' },
        { value: 'boat-shoes', label: 'Boat Shoes' },
        { value: 'deck-shoes', label: 'Deck Shoes' },
        { value: 'canvas-shoes', label: 'Canvas Shoes' },
        { value: 'converse', label: 'Converse' },
        { value: 'vans', label: 'Vans' },
        { value: 'keds', label: 'Keds' },
        { value: 'slip-on-sneakers', label: 'Slip On Sneakers' },
        { value: 'low-top-sneakers', label: 'Low Top Sneakers' },
        { value: 'high-top-sneakers', label: 'High Top Sneakers' },
        { value: 'mid-top-sneakers', label: 'Mid Top Sneakers' },
        
        // Formal & Dress
        { value: 'dress-shoes', label: 'Dress Shoes' },
        { value: 'oxford-shoes', label: 'Oxford Shoes' },
        { value: 'derby-shoes', label: 'Derby Shoes' },
        { value: 'brogue-shoes', label: 'Brogue Shoes' },
        { value: 'wingtip-shoes', label: 'Wingtip Shoes' },
        { value: 'cap-toe-shoes', label: 'Cap Toe Shoes' },
        { value: 'plain-toe-shoes', label: 'Plain Toe Shoes' },
        { value: 'monk-strap-shoes', label: 'Monk Strap Shoes' },
        { value: 'double-monk-shoes', label: 'Double Monk Shoes' },
        { value: 'patent-leather-shoes', label: 'Patent Leather Shoes' },
        { value: 'tuxedo-shoes', label: 'Tuxedo Shoes' },
        { value: 'evening-shoes', label: 'Evening Shoes' },
        { value: 'cocktail-shoes', label: 'Cocktail Shoes' },
        { value: 'formal-pumps', label: 'Formal Pumps' },
        { value: 'court-shoes', label: 'Court Shoes' },
        
        // Specialty & Unique
        { value: 'clogs', label: 'Clogs' },
        { value: 'wooden-clogs', label: 'Wooden Clogs' },
        { value: 'dutch-clogs', label: 'Dutch Clogs' },
        { value: 'crocs', label: 'Crocs' },
        { value: 'moccasins', label: 'Moccasins' },
        { value: 'slippers', label: 'Slippers' },
        { value: 'house-slippers', label: 'House Slippers' },
        { value: 'fuzzy-slippers', label: 'Fuzzy Slippers' },
        { value: 'ballet-slippers', label: 'Ballet Slippers' },
        { value: 'pointe-shoes', label: 'Pointe Shoes' },
        { value: 'dance-shoes', label: 'Dance Shoes' },
        { value: 'tap-shoes', label: 'Tap Shoes' },
        { value: 'jazz-shoes', label: 'Jazz Shoes' },
        { value: 'character-shoes', label: 'Character Shoes' },
        { value: 'ballroom-shoes', label: 'Ballroom Shoes' },
        { value: 'latin-dance-shoes', label: 'Latin Dance Shoes' },
        { value: 'salsa-shoes', label: 'Salsa Shoes' },
        { value: 'tango-shoes', label: 'Tango Shoes' },
        { value: 'flamenco-shoes', label: 'Flamenco Shoes' },
        { value: 'irish-dance-shoes', label: 'Irish Dance Shoes' },
        { value: 'clogging-shoes', label: 'Clogging Shoes' },
        
        // Fetish & Alternative
        { value: 'fetish-boots', label: 'Fetish Boots' },
        { value: 'dominatrix-boots', label: 'Dominatrix Boots' },
        { value: 'latex-boots', label: 'Latex Boots' },
        { value: 'leather-boots', label: 'Leather Boots' },
        { value: 'pvc-boots', label: 'PVC Boots' },
        { value: 'patent-boots', label: 'Patent Boots' },
        { value: 'lace-up-fetish-boots', label: 'Lace Up Fetish Boots' },
        { value: 'corset-boots', label: 'Corset Boots' },
        { value: 'extreme-heels', label: 'Extreme Heels' },
        { value: 'ballet-boots', label: 'Ballet Boots' },
        { value: 'hoof-boots', label: 'Hoof Boots' },
        { value: 'pony-boots', label: 'Pony Boots' },
        { value: 'lockable-boots', label: 'Lockable Boots' },
        { value: 'restraint-boots', label: 'Restraint Boots' },
        { value: 'spike-heels', label: 'Spike Heels' },
        { value: 'metal-heels', label: 'Metal Heels' },
        { value: 'chain-boots', label: 'Chain Boots' },
        { value: 'studded-boots', label: 'Studded Boots' },
        { value: 'buckle-boots', label: 'Buckle Boots' },
        { value: 'strap-boots', label: 'Strap Boots' },
        
        // Fantasy & Cosplay
        { value: 'fairy-shoes', label: 'Fairy Shoes' },
        { value: 'elf-shoes', label: 'Elf Shoes' },
        { value: 'pixie-boots', label: 'Pixie Boots' },
        { value: 'witch-boots', label: 'Witch Boots' },
        { value: 'wizard-boots', label: 'Wizard Boots' },
        { value: 'medieval-boots', label: 'Medieval Boots' },
        { value: 'renaissance-shoes', label: 'Renaissance Shoes' },
        { value: 'victorian-boots', label: 'Victorian Boots' },
        { value: 'steampunk-boots', label: 'Steampunk Boots' },
        { value: 'pirate-boots', label: 'Pirate Boots' },
        { value: 'knight-boots', label: 'Knight Boots' },
        { value: 'armor-boots', label: 'Armor Boots' },
        { value: 'superhero-boots', label: 'Superhero Boots' },
        { value: 'villain-boots', label: 'Villain Boots' },
        { value: 'anime-boots', label: 'Anime Boots' },
        { value: 'cosplay-shoes', label: 'Cosplay Shoes' },
        { value: 'character-boots', label: 'Character Boots' },
        { value: 'costume-shoes', label: 'Costume Shoes' },
        { value: 'themed-footwear', label: 'Themed Footwear' },
        
        // Futuristic & Sci-Fi
        { value: 'cyberpunk-boots', label: 'Cyberpunk Boots' },
        { value: 'robot-boots', label: 'Robot Boots' },
        { value: 'android-shoes', label: 'Android Shoes' },
        { value: 'space-boots', label: 'Space Boots' },
        { value: 'astronaut-boots', label: 'Astronaut Boots' },
        { value: 'zero-gravity-shoes', label: 'Zero Gravity Shoes' },
        { value: 'hover-boots', label: 'Hover Boots' },
        { value: 'jet-boots', label: 'Jet Boots' },
        { value: 'rocket-boots', label: 'Rocket Boots' },
        { value: 'magnetic-boots', label: 'Magnetic Boots' },
        { value: 'anti-gravity-boots', label: 'Anti-Gravity Boots' },
        { value: 'teleportation-boots', label: 'Teleportation Boots' },
        { value: 'time-travel-boots', label: 'Time Travel Boots' },
        { value: 'dimension-boots', label: 'Dimension Boots' },
        { value: 'quantum-shoes', label: 'Quantum Shoes' },
        { value: 'holographic-shoes', label: 'Holographic Shoes' },
        { value: 'led-shoes', label: 'LED Shoes' },
        { value: 'neon-shoes', label: 'Neon Shoes' },
        { value: 'glow-shoes', label: 'Glow Shoes' },
        { value: 'light-up-shoes', label: 'Light Up Shoes' },
        
        // Extreme & Unusual
        { value: 'invisible-shoes', label: 'Invisible Shoes' },
        { value: 'glass-slippers', label: 'Glass Slippers' },
        { value: 'crystal-shoes', label: 'Crystal Shoes' },
        { value: 'diamond-shoes', label: 'Diamond Shoes' },
        { value: 'gold-shoes', label: 'Gold Shoes' },
        { value: 'silver-shoes', label: 'Silver Shoes' },
        { value: 'platinum-shoes', label: 'Platinum Shoes' },
        { value: 'jeweled-shoes', label: 'Jeweled Shoes' },
        { value: 'ruby-slippers', label: 'Ruby Slippers' },
        { value: 'emerald-shoes', label: 'Emerald Shoes' },
        { value: 'sapphire-boots', label: 'Sapphire Boots' },
        { value: 'pearl-shoes', label: 'Pearl Shoes' },
        { value: 'coral-shoes', label: 'Coral Shoes' },
        { value: 'shell-shoes', label: 'Shell Shoes' },
        { value: 'bone-shoes', label: 'Bone Shoes' },
        { value: 'skull-boots', label: 'Skull Boots' },
        { value: 'flame-shoes', label: 'Flame Shoes' },
        { value: 'ice-shoes', label: 'Ice Shoes' },
        { value: 'smoke-shoes', label: 'Smoke Shoes' },
        { value: 'cloud-shoes', label: 'Cloud Shoes' },
        { value: 'rainbow-shoes', label: 'Rainbow Shoes' },
        { value: 'aurora-shoes', label: 'Aurora Shoes' },
        { value: 'galaxy-shoes', label: 'Galaxy Shoes' },
        { value: 'nebula-boots', label: 'Nebula Boots' },
        { value: 'star-shoes', label: 'Star Shoes' },
        { value: 'moon-boots', label: 'Moon Boots' },
        { value: 'sun-shoes', label: 'Sun Shoes' },
        { value: 'planet-shoes', label: 'Planet Shoes' },
        { value: 'cosmic-boots', label: 'Cosmic Boots' },
        { value: 'universe-shoes', label: 'Universe Shoes' },
        { value: 'infinity-boots', label: 'Infinity Boots' },
        { value: 'eternity-shoes', label: 'Eternity Shoes' },
        { value: 'dream-shoes', label: 'Dream Shoes' },
        { value: 'nightmare-boots', label: 'Nightmare Boots' },
        { value: 'fantasy-footwear', label: 'Fantasy Footwear' },
        { value: 'magical-shoes', label: 'Magical Shoes' },
        { value: 'enchanted-boots', label: 'Enchanted Boots' },
        { value: 'cursed-shoes', label: 'Cursed Shoes' },
        { value: 'blessed-boots', label: 'Blessed Boots' },
        { value: 'divine-shoes', label: 'Divine Shoes' },
        { value: 'godly-boots', label: 'Godly Boots' },
        { value: 'legendary-shoes', label: 'Legendary Shoes' },
        { value: 'mythical-boots', label: 'Mythical Boots' },
        { value: 'epic-shoes', label: 'Epic Shoes' },
        { value: 'ultimate-boots', label: 'Ultimate Boots' },
        { value: 'supreme-shoes', label: 'Supreme Shoes' },
        { value: 'transcendent-boots', label: 'Transcendent Boots' },
        { value: 'omnipotent-shoes', label: 'Omnipotent Shoes' },
        { value: 'omniscient-boots', label: 'Omniscient Boots' },
        { value: 'omnipresent-shoes', label: 'Omnipresent Shoes' }
      ]
    },
    {
      id: 'skinColor',
      title: 'Skin Tone',
      options: [
        { value: 'fair', label: 'Fair' },
        { value: 'light', label: 'Light' },
        { value: 'medium', label: 'Medium' },
        { value: 'olive', label: 'Olive' },
        { value: 'tan', label: 'Tan' },
        { value: 'dark', label: 'Dark' },
        { value: 'deep', label: 'Deep' }
      ]
    },
    {
      id: 'angle',
      title: 'Viewing Angle',
      options: [
        { value: 'top-down', label: 'Top Down' },
        { value: 'side-view', label: 'Side View' },
        { value: 'bottom-up', label: 'Bottom Up' },
        { value: 'close-up', label: 'Close Up' },
        { value: 'pedicure-view', label: 'Pedicure View' },
        { value: 'arched', label: 'Arched' },
        { value: 'pointed', label: 'Pointed' }
      ]
    },
    {
      id: 'style',
      title: 'Style',
      options: [
        { value: 'elegant', label: 'Elegant' },
        { value: 'casual', label: 'Casual' },
        { value: 'sexy', label: 'Sexy' },
        { value: 'artistic', label: 'Artistic' },
        { value: 'professional', label: 'Professional' },
        { value: 'vintage', label: 'Vintage' },
        { value: 'modern', label: 'Modern' }
      ]
    },
    {
      id: 'foodCoverage',
      title: 'Food Coverage',
      options: [
        { value: '', label: 'No Food' },
        { value: 'chocolate-sauce', label: 'Chocolate Sauce' },
        { value: 'whipped-cream', label: 'Whipped Cream' },
        { value: 'strawberries', label: 'Strawberries' },
        { value: 'caramel', label: 'Caramel' },
        { value: 'honey', label: 'Honey' },
        { value: 'maple-syrup', label: 'Maple Syrup' },
        { value: 'nutella', label: 'Nutella' },
        { value: 'ice-cream', label: 'Ice Cream' },
        { value: 'sprinkles', label: 'Sprinkles' },
        { value: 'powdered-sugar', label: 'Powdered Sugar' },
        { value: 'fruit-slices', label: 'Fruit Slices' },
        { value: 'marshmallows', label: 'Marshmallows' },
        { value: 'chocolate-chips', label: 'Chocolate Chips' },
        { value: 'candy', label: 'Candy' },
        { value: 'cake-frosting', label: 'Cake Frosting' },
        { value: 'jam', label: 'Jam' },
        { value: 'sauce', label: 'Sauce' }
      ]
    },
    {
      id: 'fantasyTransformation',
      title: 'Fantasy Transformation',
      options: [
        { value: '', label: 'Normal Feet' },
        { value: 'hobbit-feet', label: 'Hobbit Feet' },
        { value: 'chicken-feet', label: 'Chicken Feet' },
        { value: 'goat-hooves', label: 'Goat Hooves' },
        { value: 'dragon-claws', label: 'Dragon Claws' },
        { value: 'dragon-feet', label: 'Dragon Feet' },
        { value: 'yoda-feet', label: 'Yoda Feet' },
        { value: 'fairy-feet', label: 'Fairy Feet' },
        { value: 'mermaid-scales', label: 'Mermaid Scales' },
        { value: 'robot-feet', label: 'Robot Feet' },
        { value: 'alien-feet', label: 'Alien Feet' },
        { value: 'zombie-feet', label: 'Zombie Feet' },
        { value: 'vampire-feet', label: 'Vampire Feet' },
        { value: 'werewolf-paws', label: 'Werewolf Paws' },
        { value: 'unicorn-hooves', label: 'Unicorn Hooves' },
        { value: 'centaur-hooves', label: 'Centaur Hooves' },
        { value: 'satyr-hooves', label: 'Satyr Hooves' },
        { value: 'demon-claws', label: 'Demon Claws' },
        { value: 'angel-feet', label: 'Angel Feet' },
        { value: 'elf-feet', label: 'Elf Feet' },
        { value: 'dwarf-feet', label: 'Dwarf Feet' },
        { value: 'orc-feet', label: 'Orc Feet' },
        { value: 'troll-feet', label: 'Troll Feet' },
        { value: 'goblin-feet', label: 'Goblin Feet' },
        { value: 'frog-feet', label: 'Frog Feet' },
        { value: 'lizard-feet', label: 'Lizard Feet' },
        { value: 'spider-legs', label: 'Spider Legs' },
        { value: 'butterfly-feet', label: 'Butterfly Feet' },
        { value: 'bird-claws', label: 'Bird Claws' },
        { value: 'fish-fins', label: 'Fish Fins' },
        { value: 'snake-scales', label: 'Snake Scales' },
        { value: 'crystal-feet', label: 'Crystal Feet' },
        { value: 'ice-feet', label: 'Ice Feet' },
        { value: 'fire-feet', label: 'Fire Feet' },
        { value: 'shadow-feet', label: 'Shadow Feet' },
        { value: 'light-feet', label: 'Light Feet' },
        { value: 'mechanical-feet', label: 'Mechanical Feet' },
        { value: 'steampunk-feet', label: 'Steampunk Feet' },
        { value: 'cyberpunk-feet', label: 'Cyberpunk Feet' },
        { value: 'magical-feet', label: 'Magical Feet' },
        { value: 'enchanted-feet', label: 'Enchanted Feet' }
      ]
    },
    {
      id: 'glamorEffects',
      title: 'Glamor Effects',
      options: [
        { value: '', label: 'No Glamor' },
        { value: 'haze', label: 'Haze' },
        { value: 'glitter', label: 'Glitter' },
        { value: 'sparkles', label: 'Sparkles' },
        { value: 'golden-glow', label: 'Golden Glow' },
        { value: 'silver-shimmer', label: 'Silver Shimmer' },
        { value: 'rose-gold', label: 'Rose Gold' },
        { value: 'iridescent', label: 'Iridescent' },
        { value: 'metallic', label: 'Metallic' },
        { value: 'pearl-lustre', label: 'Pearl Lustre' },
        { value: 'crystal-clear', label: 'Crystal Clear' },
        { value: 'diamond-sparkle', label: 'Diamond Sparkle' },
        { value: 'aurora-borealis', label: 'Aurora Borealis' },
        { value: 'stardust', label: 'Stardust' },
        { value: 'moonlight', label: 'Moonlight' },
        { value: 'sunset-glow', label: 'Sunset Glow' },
        { value: 'neon-glow', label: 'Neon Glow' },
        { value: 'cosmic-dust', label: 'Cosmic Dust' },
        { value: 'fairy-dust', label: 'Fairy Dust' },
        { value: 'magical-mist', label: 'Magical Mist' }
      ]
    },
    {
      id: 'commonFilters',
      title: 'Common Filters',
      options: [
        { value: '', label: 'No Filter' },
        { value: 'vintage', label: 'Vintage' },
        { value: 'black-and-white', label: 'Black & White' },
        { value: 'sepia', label: 'Sepia' },
        { value: 'warm', label: 'Warm' },
        { value: 'cool', label: 'Cool' },
        { value: 'bright', label: 'Bright' },
        { value: 'soft', label: 'Soft' },
        { value: 'dramatic', label: 'Dramatic' },
        { value: 'romantic', label: 'Romantic' },
        { value: 'mysterious', label: 'Mysterious' },
        { value: 'ethereal', label: 'Ethereal' },
        { value: 'dreamy', label: 'Dreamy' },
        { value: 'cinematic', label: 'Cinematic' },
        { value: 'artistic', label: 'Artistic' },
        { value: 'professional', label: 'Professional' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'vsco', label: 'VSCO' },
        { value: 'film-grain', label: 'Film Grain' },
        { value: 'blur', label: 'Blur' },
        { value: 'sharp', label: 'Sharp' },
        { value: 'high-contrast', label: 'High Contrast' },
        { value: 'low-contrast', label: 'Low Contrast' },
        { value: 'saturated', label: 'Saturated' },
        { value: 'desaturated', label: 'Desaturated' },
        { value: 'monochrome', label: 'Monochrome' },
        { value: 'duotone', label: 'Duotone' },
        { value: 'cross-process', label: 'Cross Process' },
        { value: 'lomo', label: 'Lomo' },
        { value: 'polaroid', label: 'Polaroid' },
        { value: 'retro', label: 'Retro' },
        { value: 'modern', label: 'Modern' },
        { value: 'minimalist', label: 'Minimalist' },
        { value: 'maximalist', label: 'Maximalist' }
      ]
    },
    {
      id: 'photoEffects',
      title: 'Photo Effects',
      options: [
        { value: '', label: 'No Effects' },
        { value: 'rainbow', label: 'Rainbow' },
        { value: 'smoke', label: 'Smoke' },
        { value: 'pyrotechnics', label: 'Pyrotechnics' },
        { value: 'fireworks', label: 'Fireworks' },
        { value: 'lightning', label: 'Lightning' },
        { value: 'electricity', label: 'Electricity' },
        { value: 'plasma', label: 'Plasma' },
        { value: 'neon-trails', label: 'Neon Trails' },
        { value: 'laser-beams', label: 'Laser Beams' },
        { value: 'hologram', label: 'Hologram' },
        { value: 'energy-field', label: 'Energy Field' },
        { value: 'force-field', label: 'Force Field' },
        { value: 'portal', label: 'Portal' },
        { value: 'vortex', label: 'Vortex' },
        { value: 'tornado', label: 'Tornado' },
        { value: 'explosion', label: 'Explosion' },
        { value: 'implosion', label: 'Implosion' },
        { value: 'black-hole', label: 'Black Hole' },
        { value: 'supernova', label: 'Supernova' },
        { value: 'cosmic-storm', label: 'Cosmic Storm' },
        { value: 'time-warp', label: 'Time Warp' },
        { value: 'dimension-rift', label: 'Dimension Rift' },
        { value: 'reality-bend', label: 'Reality Bend' }
      ]
    },
    {
      id: 'nailLength',
      title: 'Nail Length',
      options: [
        { value: '', label: 'Natural Length' },
        { value: 'short', label: 'Short' },
        { value: 'medium', label: 'Medium' },
        { value: 'long', label: 'Long' },
        { value: 'extra-long', label: 'Extra Long' },
        { value: 'stiletto', label: 'Stiletto' },
        { value: 'coffin', label: 'Coffin' },
        { value: 'almond', label: 'Almond' },
        { value: 'square', label: 'Square' },
        { value: 'round', label: 'Round' },
        { value: 'oval', label: 'Oval' },
        { value: 'ballerina', label: 'Ballerina' },
        { value: 'lipstick', label: 'Lipstick' },
        { value: 'edge', label: 'Edge' },
        { value: 'flared', label: 'Flared' },
        { value: 'duck', label: 'Duck' },
        { value: 'mountain-peak', label: 'Mountain Peak' },
        { value: 'arrowhead', label: 'Arrowhead' },
        { value: 'dagger', label: 'Dagger' },
        { value: 'claw', label: 'Claw' },
        { value: 'talon', label: 'Talon' },
        { value: 'fang', label: 'Fang' },
        { value: 'blade', label: 'Blade' }
      ]
    },
    {
      id: 'furTexture',
      title: 'Fur Texture',
      options: [
        { value: '', label: 'No Fur' },
        { value: 'soft-fur', label: 'Soft Fur' },
        { value: 'thick-fur', label: 'Thick Fur' },
        { value: 'fluffy-fur', label: 'Fluffy Fur' },
        { value: 'smooth-fur', label: 'Smooth Fur' },
        { value: 'coarse-fur', label: 'Coarse Fur' },
        { value: 'silky-fur', label: 'Silky Fur' },
        { value: 'wiry-fur', label: 'Wiry Fur' },
        { value: 'curly-fur', label: 'Curly Fur' },
        { value: 'straight-fur', label: 'Straight Fur' },
        { value: 'wavy-fur', label: 'Wavy Fur' },
        { value: 'spiky-fur', label: 'Spiky Fur' },
        { value: 'bristly-fur', label: 'Bristly Fur' },
        { value: 'velvety-fur', label: 'Velvety Fur' },
        { value: 'plush-fur', label: 'Plush Fur' },
        { value: 'shaggy-fur', label: 'Shaggy Fur' },
        { value: 'matted-fur', label: 'Matted Fur' },
        { value: 'groomed-fur', label: 'Groomed Fur' },
        { value: 'wild-fur', label: 'Wild Fur' },
        { value: 'luxurious-fur', label: 'Luxurious Fur' },
        { value: 'exotic-fur', label: 'Exotic Fur' },
        { value: 'mystical-fur', label: 'Mystical Fur' },
        { value: 'cosmic-fur', label: 'Cosmic Fur' },
        { value: 'neon-fur', label: 'Neon Fur' },
        { value: 'metallic-fur', label: 'Metallic Fur' },
        { value: 'crystal-fur', label: 'Crystal Fur' }
      ]
    },
    {
      id: 'surfaceTexture',
      title: 'Surface Texture',
      options: [
        { value: '', label: 'Natural Skin' },
        { value: 'smooth', label: 'Smooth' },
        { value: 'rough', label: 'Rough' },
        { value: 'bumpy', label: 'Bumpy' },
        { value: 'wrinkled', label: 'Wrinkled' },
        { value: 'scaly', label: 'Scaly' },
        { value: 'leathery', label: 'Leathery' },
        { value: 'velvety', label: 'Velvety' },
        { value: 'suede', label: 'Suede' },
        { value: 'satin', label: 'Satin' },
        { value: 'silk', label: 'Silk' },
        { value: 'cotton', label: 'Cotton' },
        { value: 'wool', label: 'Wool' },
        { value: 'denim', label: 'Denim' },
        { value: 'canvas', label: 'Canvas' },
        { value: 'cork', label: 'Cork' },
        { value: 'wood', label: 'Wood' },
        { value: 'stone', label: 'Stone' },
        { value: 'marble', label: 'Marble' },
        { value: 'granite', label: 'Granite' },
        { value: 'crystal', label: 'Crystal' },
        { value: 'glass', label: 'Glass' },
        { value: 'metal', label: 'Metal' },
        { value: 'plastic', label: 'Plastic' },
        { value: 'rubber', label: 'Rubber' },
        { value: 'ceramic', label: 'Ceramic' },
        { value: 'porcelain', label: 'Porcelain' },
        { value: 'concrete', label: 'Concrete' },
        { value: 'asphalt', label: 'Asphalt' },
        { value: 'sand', label: 'Sand' },
        { value: 'mud', label: 'Mud' },
        { value: 'ice', label: 'Ice' },
        { value: 'frost', label: 'Frost' },
        { value: 'lava', label: 'Lava' },
        { value: 'obsidian', label: 'Obsidian' },
        { value: 'diamond', label: 'Diamond' },
        { value: 'gold', label: 'Gold' },
        { value: 'silver', label: 'Silver' },
        { value: 'bronze', label: 'Bronze' },
        { value: 'copper', label: 'Copper' },
        { value: 'steel', label: 'Steel' },
        { value: 'titanium', label: 'Titanium' },
        { value: 'chrome', label: 'Chrome' },
        { value: 'brass', label: 'Brass' },
        { value: 'aluminum', label: 'Aluminum' },
        { value: 'carbon-fiber', label: 'Carbon Fiber' },
        { value: 'kevlar', label: 'Kevlar' },
        { value: 'neoprene', label: 'Neoprene' },
        { value: 'latex', label: 'Latex' },
        { value: 'vinyl', label: 'Vinyl' },
        { value: 'silicone', label: 'Silicone' },
        { value: 'tpe', label: 'TPE' },
        { value: 'tpu', label: 'TPU' },
        { value: 'abs', label: 'ABS' },
        { value: 'pvc', label: 'PVC' },
        { value: 'nylon', label: 'Nylon' },
        { value: 'polyester', label: 'Polyester' },
        { value: 'acrylic', label: 'Acrylic' },
        { value: 'polycarbonate', label: 'Polycarbonate' },
        { value: 'fiberglass', label: 'Fiberglass' },
        { value: 'carbon-nanotubes', label: 'Carbon Nanotubes' },
        { value: 'graphene', label: 'Graphene' },
        { value: 'quantum-material', label: 'Quantum Material' },
        { value: 'dark-matter', label: 'Dark Matter' },
        { value: 'antimatter', label: 'Antimatter' },
        { value: 'plasma', label: 'Plasma' },
        { value: 'energy', label: 'Energy' },
        { value: 'force', label: 'Force' },
        { value: 'magic', label: 'Magic' },
        { value: 'void', label: 'Void' },
        { value: 'ethereal', label: 'Ethereal' },
        { value: 'cosmic', label: 'Cosmic' },
        { value: 'dimensional', label: 'Dimensional' },
        { value: 'temporal', label: 'Temporal' },
        { value: 'spatial', label: 'Spatial' },
        { value: 'quantum', label: 'Quantum' },
        { value: 'subatomic', label: 'Subatomic' },
        { value: 'nano', label: 'Nano' },
        { value: 'micro', label: 'Micro' },
        { value: 'macro', label: 'Macro' },
        { value: 'mega', label: 'Mega' },
        { value: 'giga', label: 'Giga' },
        { value: 'tera', label: 'Tera' },
        { value: 'peta', label: 'Peta' },
        { value: 'exa', label: 'Exa' },
        { value: 'zetta', label: 'Zetta' },
        { value: 'yotta', label: 'Yotta' }
      ]
    },
    {
      id: 'bondage',
      title: 'Bondage & Fetish',
      options: [
        { value: '', label: 'No Bondage' },
        { value: 'latex', label: 'Latex' },
        { value: 'leather', label: 'Leather' },
        { value: 'studs', label: 'Studs' },
        { value: 'chains', label: 'Chains' },
        { value: 'rope', label: 'Rope' },
        { value: 'cuffs', label: 'Cuffs' },
        { value: 'harness', label: 'Harness' },
        { value: 'gags', label: 'Gags' },
        { value: 'tape', label: 'Tape' },
        { value: 'fishnet', label: 'Fishnet' },
        { value: 'mesh', label: 'Mesh' },
        { value: 'zentai', label: 'Zentai' },
        { value: 'boots', label: 'Boots' },
        { value: 'heels', label: 'Heels' },
        { value: 'collar', label: 'Collar' },
        { value: 'blindfold', label: 'Blindfold' },
        { value: 'ballgag', label: 'Ballgag' },
        { value: 'clamps', label: 'Clamps' },
        { value: 'spikes', label: 'Spikes' },
        { value: 'rings', label: 'Rings' },
        { value: 'straps', label: 'Straps' },
        { value: 'corset', label: 'Corset' },
        { value: 'bodysuit', label: 'Bodysuit' },
        { value: 'garter', label: 'Garter' },
        { value: 'pasties', label: 'Pasties' },
        { value: 'nipple-covers', label: 'Nipple Covers' },
        { value: 'open-toe', label: 'Open Toe' },
        { value: 'platforms', label: 'Platforms' },
        { value: 'fetish-wear', label: 'Fetish Wear' }
      ]
    },
    {
      id: 'occupation',
      title: 'Occupation',
      options: [
        { value: '', label: 'No Occupation' },
        { value: 'lawyer', label: 'Lawyer' },
        { value: 'doctor', label: 'Doctor' },
        { value: 'nurse', label: 'Nurse' },
        { value: 'teacher', label: 'Teacher' },
        { value: 'police-officer', label: 'Police Officer' },
        { value: 'firefighter', label: 'Firefighter' },
        { value: 'astronaut', label: 'Astronaut' },
        { value: 'scientist', label: 'Scientist' },
        { value: 'artist', label: 'Artist' },
        { value: 'musician', label: 'Musician' },
        { value: 'dancer', label: 'Dancer' },
        { value: 'athlete', label: 'Athlete' },
        { value: 'chef', label: 'Chef' },
        { value: 'pilot', label: 'Pilot' },
        { value: 'engineer', label: 'Engineer' },
        { value: 'mechanic', label: 'Mechanic' },
        { value: 'gamer', label: 'Gamer' },
        { value: 'influencer', label: 'Influencer' },
        { value: 'model', label: 'Model' },
        { value: 'photographer', label: 'Photographer' },
        { value: 'bartender', label: 'Bartender' },
        { value: 'barista', label: 'Barista' },
        { value: 'librarian', label: 'Librarian' },
        { value: 'writer', label: 'Writer' },
        { value: 'poet', label: 'Poet' },
        { value: 'magician', label: 'Magician' },
        { value: 'clown', label: 'Clown' },
        { value: 'jester', label: 'Jester' },
        { value: 'pirate', label: 'Pirate' },
        { value: 'knight', label: 'Knight' },
        { value: 'queen', label: 'Queen' },
        { value: 'king', label: 'King' },
        { value: 'princess', label: 'Princess' },
        { value: 'prince', label: 'Prince' },
        { value: 'witch', label: 'Witch' },
        { value: 'wizard', label: 'Wizard' },
        { value: 'superhero', label: 'Superhero' },
        { value: 'villain', label: 'Villain' },
        { value: 'detective', label: 'Detective' },
        { value: 'spy', label: 'Spy' },
        { value: 'agent', label: 'Agent' },
        { value: 'hacker', label: 'Hacker' },
        { value: 'programmer', label: 'Programmer' },
        { value: 'streamer', label: 'Streamer' },
        { value: 'movie-projectionist', label: 'Movie Projectionist' },
        { value: 'fry-cook', label: 'Fry Cook' },
        { value: 'dominatrix', label: 'Dominatrix' }
      ]
    },
    {
      id: 'toeHat',
      title: 'Toe Hats',
      options: [
        { value: '', label: 'No Hat' },
        { value: 'top-hat', label: 'Top Hat' },
        { value: 'baseball-cap', label: 'Baseball Cap' },
        { value: 'beanie', label: 'Beanie' },
        { value: 'crown', label: 'Crown' },
        { value: 'tiara', label: 'Tiara' },
        { value: 'party-hat', label: 'Party Hat' },
        { value: 'fedora', label: 'Fedora' },
        { value: 'cowboy-hat', label: 'Cowboy Hat' },
        { value: 'beret', label: 'Beret' },
        { value: 'sombrero', label: 'Sombrero' },
        { value: 'wizard-hat', label: 'Wizard Hat' },
        { value: 'chef-hat', label: 'Chef Hat' },
        { value: 'santa-hat', label: 'Santa Hat' },
        { value: 'bunny-ears', label: 'Bunny Ears' },
        { value: 'devil-horns', label: 'Devil Horns' },
        { value: 'halo', label: 'Halo' },
        { value: 'helmet', label: 'Helmet' },
        { value: 'pirate-hat', label: 'Pirate Hat' },
        { value: 'jester-hat', label: 'Jester Hat' }
      ]
    },
    {
      id: 'paraphernalia',
      title: 'Illicit Paraphernalia',
      options: [
        { value: '', label: 'None' },
        { value: 'meth-pipe', label: 'Meth Pipe' },
        { value: 'joint', label: 'Joint' },
        { value: 'bong', label: 'Bong' },
        { value: 'pills', label: 'Pills' },
        { value: 'powder', label: 'Powder' },
        { value: 'rolled-bill', label: 'Rolled Bill' },
        { value: 'syringe', label: 'Syringe' },
        { value: 'vape', label: 'Vape' },
        { value: 'blunt', label: 'Blunt' },
        { value: 'cocaine-spoon', label: 'Cocaine Spoon' },
        { value: 'stash-bag', label: 'Stash Bag' },
        { value: 'weed-leaf', label: 'Weed Leaf' },
        { value: 'crack-rock', label: 'Crack Rock' },
        { value: 'acid-tab', label: 'Acid Tab' },
        { value: 'shrooms', label: 'Shrooms' },
        { value: 'needle', label: 'Needle' },
        { value: 'lighter', label: 'Lighter' },
        { value: 'spoon', label: 'Spoon' },
        { value: 'foil', label: 'Foil' }
      ]
    }
  ]

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId)
    } else {
      newExpanded.add(categoryId)
    }
    setExpandedCategories(newExpanded)
  }

  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case 'background':
        return <Camera className="w-4 h-4" />
      case 'nailColor':
        return <Palette className="w-4 h-4" />
      case 'jewelry':
        return <Gem className="w-4 h-4" />
      case 'shoes':
        return <Footprints className="w-4 h-4" />
      case 'skinColor':
        return <Sparkles className="w-4 h-4" />
      case 'angle':
        return <Camera className="w-4 h-4" />
      case 'style':
        return <Sparkles className="w-4 h-4" />
      case 'foodCoverage':
        return <Utensils className="w-4 h-4" />
      case 'fantasyTransformation':
        return <Zap className="w-4 h-4" />
      case 'glamorEffects':
        return <Star className="w-4 h-4" />
      case 'commonFilters':
        return <Filter className="w-4 h-4" />
      case 'photoEffects':
        return <Lightning className="w-4 h-4" />
      case 'nailLength':
        return <Scissors className="w-4 h-4" />
      case 'furTexture':
        return <Feather className="w-4 h-4" />
      case 'surfaceTexture':
        return <Layers className="w-4 h-4" />
      case 'bondage':
        return <Lock className="w-4 h-4" />
      case 'occupation':
        return <Briefcase className="w-4 h-4" />
      case 'toeHat':
        return <PartyPopper className="w-4 h-4" />
      case 'paraphernalia':
        return <Pill className="w-4 h-4" />
      default:
        return <Sparkles className="w-4 h-4" />
    }
  }

  // Calculate total active filters across all categories
  const getTotalActiveFilters = () => {
    return Object.values(filters).reduce((total, filterArray) => total + filterArray.length, 0)
  }

  // Reset all filters
  const handleResetAllFilters = () => {
    // Reset each filter category by calling onFilterChange with empty string
    Object.keys(filters).forEach(filterType => {
      onFilterChange(filterType as keyof FilterOptions, '')
    })
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 animate-fade-in">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize Your Image</h3>
      
      {/* Global Reset All Filters Button */}
      {getTotalActiveFilters() > 0 && (
        <div className="mb-6">
          <button
            onClick={handleResetAllFilters}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 hover:border-red-300 transition-all duration-200 font-medium"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset All Filters ({getTotalActiveFilters()})</span>
          </button>
        </div>
      )}
      
      <div className="space-y-4">
        {filterCategories.map((category) => {
          const isExpanded = expandedCategories.has(category.id)
          const selectedValues = filters[category.id] as string[]
          
          return (
            <div key={category.id} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  {getCategoryIcon(category.id)}
                  <span className="font-medium text-gray-900">{category.title}</span>
                  {selectedValues.length > 0 && (
                    <span className="bg-primary-500 text-white text-xs px-2 py-1 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                      {selectedValues.length}
                    </span>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                )}
              </button>
              
              {isExpanded && (
                <div className="p-4 bg-white">
                  {/* Clear All Button */}
                  {selectedValues.length > 0 && (
                    <div className="mb-3">
                      <button
                        onClick={() => onFilterChange(category.id, '')}
                        className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Clear All ({selectedValues.length})
                      </button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    {category.options.map((option) => {
                      const isSelected = selectedValues.includes(option.value)
                      return (
                        <button
                          key={option.value}
                          onClick={() => onFilterChange(category.id, option.value)}
                          className={`px-3 py-2 text-sm rounded-lg border transition-all duration-200 text-left ${
                            isSelected 
                              ? 'bg-primary-500 text-white border-primary-500 shadow-md' 
                              : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FilterPanel 